import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import BondAgreementPage from "./pages/BondAgreementPage";
import SignatureAgreementPage from "./pages/SignatureAgreementPage";
import LawyerAgreementPage from "./pages/LawyerAgreementPage";
import KakaoAuthPage from "./pages/KakaoAuthPage";
import IdCardSubmitPage from "./pages/IdCardSubmitPage";
import SubmissionConfirmPage from "./pages/SubmissionConfirmPage";
import CompletionPage from "./pages/CompletionPage";
import IdCardCameraPage from "./pages/IdCardCameraPage";
import IdCardCropPage from "./pages/IdCardCropPage";
import "./App.css";
import WelcomePage from "./pages/WelcomePage";
import { AuthProvider } from "./contexts/AuthContext";
import NotFoundPage from "./pages/NotFoundPage";
import Cookies from "js-cookie";
import API_ENDPOINTS from "./config/api";
import SubmissionCompletePage from "./pages/SubmissionCompletePage";
import CoOwnerCheckPage from "./pages/CoOwnerCheckPage";
import CoOwnerInfoPage from "./pages/CoOwnerInfoPage";
import CoOwnerInitPage from "./pages/CoOwnerInitPage";
import KakaoLoginPage from "./pages/KakaoLoginPage";
import SubmissionCheckPage from "./pages/SubmissionCheckPage";
import SubmissionResultPage from "./pages/SubmissionResultPage";
import ApplicantInfoPageV2 from "./pages/ApplicantInfoPageV2";
import axios from "axios";
import InquiryButton from "./components/InquiryButton";
import { initPreventBack, preventBack } from "./utils/navigation";

// 보호된 라우트 컴포넌트
const ProtectedRoute = ({ children }) => {
  const [showErrorModal, setShowErrorModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const accessToken = Cookies.get(
      API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN
    );

    if (!accessToken) {
      setShowErrorModal(true);
      setTimeout(() => {
        setShowErrorModal(false);
        navigate("/");
      }, 2000);
    }
  }, [navigate]);

  const accessToken = Cookies.get(
    API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN
  );

  if (!accessToken) {
    return (
      <>
        {children}
        {showErrorModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 1)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            {/* <div
              className="error-modal"
              style={{
                position: "relative",
                backgroundColor: "#ff4d4f",
                color: "white",
                padding: "12px 24px",
                borderRadius: "8px",
                width: "90%",
                maxWidth: "400px",
                textAlign: "center",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              }}
            >
              <p style={{ margin: 0, fontSize: "14px", lineHeight: "1.4" }}>
                로그인 정보를 찾을 수 없습니다.
              </p>
            </div> */}
          </div>
        )}
      </>
    );
  }

  return children;
};

// 만료 시간이 있는 로컬 스토리지 도우미 함수
const storageWithExpiry = {
  set: (key, value, ttl) => {
    const now = new Date();
    const item = {
      value,
      expiry: now.getTime() + ttl,
    };
    localStorage.setItem(key, JSON.stringify(item));
  },
  get: (key) => {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) return null;

    const item = JSON.parse(itemStr);
    const now = new Date();

    // 만료 시간 확인
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  },
  remove: (key) => {
    localStorage.removeItem(key);
  },
};

// 카카오톡 인앱뷰 안내 메시지 컴포넌트
const KakaoRedirectPage = () => {
  const [redirectComplete, setRedirectComplete] = useState(false);

  useEffect(() => {
    // 이미 리다이렉트를 시도한 경우에도 계속 페이지 유지
    const hasRedirected = storageWithExpiry.get("redirectAttempted") === true;
    if (hasRedirected) {
      setRedirectComplete(true);
      return;
    }

    // 리다이렉트 시도 기록 (5분 만료)
    const FIVE_MINUTES = 1 * 6 * 1000;
    storageWithExpiry.set("redirectAttempted", true, FIVE_MINUTES);

    // 페이지 로드 시 직접 외부 브라우저로 리다이렉트
    const urlToOpen =
      sessionStorage.getItem("originalUrl") || window.location.origin;
    const openExternalUrl = `kakaotalk://web/openExternal?url=${encodeURIComponent(
      urlToOpen
    )}`;

    // 약간의 지연 후 실행 (화면 렌더링 이후)
    const redirectTimer = setTimeout(() => {
      window.location.href = openExternalUrl;

      // 두 번째 방식 시도 (첫 번째가 실패할 경우)
      const fallbackTimer = setTimeout(() => {
        // 다른 방식의 리다이렉트 시도
        window.open(urlToOpen, "_system");
        setRedirectComplete(true);
      }, 1000);

      return () => clearTimeout(fallbackTimer);
    }, 300);

    return () => clearTimeout(redirectTimer);
  }, []);

  // 애니메이션 스타일 정의
  const styles = {
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      padding: "0 20px",
      textAlign: "center",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
      background: "linear-gradient(to bottom, #f8f9fa, #e9ecef)",
      color: "#333",
    },
    card: {
      width: "100%",
      maxWidth: "380px",
      padding: "35px 30px",
      borderRadius: "16px",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.05)",
      background: "white",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    iconContainer: {
      marginBottom: "25px",
    },
    icon: {
      width: "60px",
      height: "60px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
      background: redirectComplete ? "#e6f7ff" : "#e6f7ff",
      color: redirectComplete ? "#0095f6" : "#0095f6",
      marginBottom: "5px",
    },
    title: {
      fontSize: "24px",
      fontWeight: "700",
      marginBottom: "20px",
      color: "#212529",
    },
    description: {
      fontSize: "16px",
      lineHeight: "1.7",
      marginBottom: "30px",
      color: "#495057",
      wordBreak: "keep-all",
    },
    loadingSpinner: {
      width: "22px",
      height: "22px",
      border: "3px solid #f0f0f0",
      borderTopColor: "#0095f6",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    footer: {
      fontSize: "14px",
      color: "#868e96",
      marginTop: "25px",
      opacity: "0.8",
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.iconContainer}>
          <div style={styles.icon}>
            {redirectComplete ? (
              // 브라우저 아이콘
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 4h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zm1 2v12h14V6H5zm7 3l4 4-4 4v-3H8v-2h4V9z"
                  fill="#0095f6"
                />
              </svg>
            ) : (
              // 화살표 아이콘
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.004 9.414l-8.607 8.607-1.414-1.414L14.589 8H7.004V6h11v11h-2V9.414z"
                  fill="#0095f6"
                />
              </svg>
            )}
          </div>
        </div>

        <h1 style={styles.title}>
          {redirectComplete
            ? "외부 브라우저에서 처리 중"
            : "외부 브라우저로 이동 중입니다"}
        </h1>

        <p style={styles.description}>
          {redirectComplete ? (
            <>
              공동소유 위임 동의 절차가 외부 브라우저에서 진행 중입니다.
              <br />
              카카오톡 앱 내에서는 진행할 수 없으며, 외부 브라우저에서 절차를
              완료해 주세요.
            </>
          ) : (
            <>
              공동소유 위임 동의 진행을 위해 외부 브라우저로 이동됩니다.
              <br />
              안전한 인증 및 처리를 위해 외부 브라우저에서 절차를 완료해 주세요.
            </>
          )}
        </p>

        {!redirectComplete && <div style={styles.loadingSpinner}></div>}

        {redirectComplete && (
          <div style={styles.footer}>이 창은 닫으셔도 됩니다</div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          body {
            margin: 0;
            padding: 0;
            background: #f8f9fa;
          }
        `}
      </style>
    </div>
  );
};

// NavigationGuard 컴포넌트 - 뒤로가기 방지 기능
const NavigationGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // 현재 페이지 URL 저장
    const currentPathname = location.pathname;

    // 뒤로가기 방지 초기화
    initPreventBack();

    // 뒤로가기 이벤트 감지 설정 및 정리 함수 저장
    const cleanup = preventBack(currentPathname, navigate);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return cleanup;
  }, [location, navigate]);

  return null; // UI는 없음
};

// 애니메이션이 있는 라우터 컴포넌트
const AnimatedRoutes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const {
    KEYS: COOKIE_KEYS,
    EXPIRES: COOKIE_EXPIRES,
    OPTIONS: COOKIE_OPTIONS,
  } = API_ENDPOINTS.COOKIE_CONFIG;
  const topRef = useRef(null);

  // 클레임 상태 확인 및 리다이렉트
  // useEffect(() => {
  //   const checkClaimStatus = async () => {
  //     try {
  //       const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
  //       if (!accessToken) return;

  //       const response = await axios({
  //         method: "GET",
  //         url: `${API_ENDPOINTS.BASE_URL}/mobile/claim`,
  //         headers: {
  //           Authorization: `Bearer ${accessToken}`,
  //         },
  //       });

  //       const claimData = response.data;

  //       if (claimData) {
  //         const {
  //           claimAgree,
  //           claimSignature,
  //           sharedOwner,
  //           claimIdCard,
  //           claimStatus,
  //         } = claimData;

  //         // 모든 조건이 충족되면 제출 완료 페이지로 이동
  //         if (!claimAgree) {
  //           return;
  //         }
  //         if (!claimSignature && !sharedOwner) {
  //           navigate("/bond-agreement", { replace: true });
  //           return;
  //         }
  //         if (!claimSignature && sharedOwner) {
  //           navigate("/bond-agreement", { replace: true });
  //           return;
  //         }
  //         if (claimAgree && claimSignature && claimIdCard) {
  //           navigate("/submission-complete", {
  //             replace: true,
  //             state: {
  //               isWaitingCoOwner: sharedOwner && claimStatus === "processing",
  //             },
  //           });
  //           return;
  //         }

  //         // 신분증 제출이 안되었으면 ID 카드 제출 페이지로 이동
  //         if (claimStatus === "processing" && !claimIdCard) {
  //           navigate("/id-card-submit", { replace: true });
  //           return;
  //         }
  //       }
  //     } catch (error) {
  //       // 에러 발생 시 무시
  //     } finally {
  //       setInitialCheckDone(true);
  //     }
  //   };

  //   if (!initialCheckDone) {
  //     checkClaimStatus();
  //   }
  // }, [navigate, initialCheckDone, COOKIE_KEYS.ACCESS_TOKEN]);

  // 컴포넌트 마운트 시 이미지 데이터 초기화
  useEffect(() => {
    // 앱 시작 시 이미지 관련 스토리지 데이터 초기화
    try {
      // localStorage에서 이미지 데이터만 제거하고 사용자 정보는 유지
      // Cookies.remove(COOKIE_KEYS.ACCESS_TOKEN, { ...COOKIE_OPTIONS });
      // Cookies.remove(COOKIE_KEYS.REFRESH_TOKEN, { ...COOKIE_OPTIONS });
      // Cookies.remove(COOKIE_KEYS.USER_INFO, { ...COOKIE_OPTIONS });
      localStorage.removeItem("temp_captured_image");
    } catch (err) {}
  }, []);
  useLayoutEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({
        behavior: "auto",
        block: "start",
      });
    }
  }, []);
  return (
    <div ref={topRef} className="app-container">
      {/* NavigationGuard 컴포넌트 추가 */}
      <NavigationGuard />

      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<WelcomePage />} />
          <Route path="/kakao-login" element={<KakaoLoginPage />} />
          <Route
            path="/submission-check"
            element={
              <ProtectedRoute>
                <SubmissionCheckPage />
              </ProtectedRoute>
            }
          />
          <Route path="/submission-result" element={<SubmissionResultPage />} />
          <Route path="/co-owner-check" element={<CoOwnerCheckPage />} />
          <Route
            path="/co-owner-info"
            element={
              <ProtectedRoute>
                <CoOwnerInfoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/co-owner-init"
            element={
              <ProtectedRoute>
                <CoOwnerInitPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/applicant-info-v2"
            element={
              <ProtectedRoute>
                <ApplicantInfoPageV2 />
              </ProtectedRoute>
            }
          />

          <Route
            path="/bond-agreement"
            element={
              <ProtectedRoute>
                <BondAgreementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/signature-agreement"
            element={
              <ProtectedRoute>
                <SignatureAgreementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lawyer-agreement"
            element={
              <ProtectedRoute>
                <LawyerAgreementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kakao-auth"
            element={
              <ProtectedRoute>
                <KakaoAuthPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/id-card-submit"
            element={
              <ProtectedRoute>
                <IdCardSubmitPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/id-card-camera"
            element={
              <ProtectedRoute>
                <IdCardCameraPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/id-card-crop"
            element={
              <ProtectedRoute>
                <IdCardCropPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/submission-confirm"
            element={
              <ProtectedRoute>
                <SubmissionConfirmPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/completion"
            element={
              <ProtectedRoute>
                <CompletionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/submission-complete"
            element={
              <ProtectedRoute>
                <SubmissionCompletePage />
              </ProtectedRoute>
            }
          />
          <Route path="/redirect-info" element={<KakaoRedirectPage />} />
          <Route path="/not-found" element={<NotFoundPage />} />
          <Route path="*" element={<Navigate to="/not-found" replace />} />
        </Routes>
      </AnimatePresence>
    </div>
  );
};

function App() {
  const [isKakao, setIsKakao] = useState(false);

  // 카카오톡 인앱뷰 감지 및 외부 브라우저로 리다이렉트
  useEffect(() => {
    const isKakaoInAppBrowser = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      return userAgent.indexOf("kakaotalk") > -1;
    };

    const kakaoDetected = isKakaoInAppBrowser();
    setIsKakao(kakaoDetected);

    // 카카오톡 인앱뷰 감지 시 항상 리다이렉트 페이지로 이동
    if (kakaoDetected && !window.location.pathname.includes("/redirect-info")) {
      // 현재 URL 저장 (아직 리다이렉트 시도하지 않은 경우에만)
      if (storageWithExpiry.get("redirectAttempted") !== true) {
        const currentUrl = window.location.href;
        sessionStorage.setItem("originalUrl", currentUrl);
      }

      // 리다이렉트 정보 페이지로 이동 (항상)
      window.location.replace(window.location.origin + "/redirect-info");
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
        <AnimatedRoutes />
        {/* <InquiryButton /> */}
      </AuthProvider>
    </Router>
  );
}

export default App;
